import React, { useEffect, useRef, useState } from 'react';
import styles from '../Recursion.module.css';
import { RecursionCommands } from '../../../types/RecursionCommands';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

type Step = {
  left: number;
  right: number;
  leftChar: string;
  rightChar: string;
  match: boolean;
  phase: 'checking' | 'match' | 'mismatch' | 'complete';
  depth: number;
};

type CallStackItem = {
  call: string;
  status: 'pending' | 'resolved';
  result?: boolean;
};

interface Props {
  command: RecursionCommands | null;
  onComplete: () => void;
  inputValue: string;
  setActiveLine: React.Dispatch<React.SetStateAction<number | null>>;
  isAnimating: boolean;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setFinalResult: React.Dispatch<React.SetStateAction<boolean | null>>;
  finalResult: boolean | null;
}

const PalindromeEngine: React.FC<Props> = ({
  command,
  onComplete,
  inputValue,
  setActiveLine,
  isAnimating,
  setIsAnimating,
  setFinalResult,
  finalResult,
}) => {
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [callStack, setCallStack] = useState<CallStackItem[]>([]);
  const [visualString, setVisualString] = useState<string[]>([]);
  const stopRef = useRef(false);

  useEffect(() => {
    if (command) executeCommand(command);
  }, [command]);

  const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  const reset = () => {
    stopRef.current = true;
    setCurrentStep(null);
    setSteps([]);
    setCallStack([]);
    setVisualString([]);
    setActiveLine(null);
  };

  const recurse = async (str: string, left: number, right: number, depth: number): Promise<boolean> => {
    if (stopRef.current) throw new Error('stopped');
    
    const callStr = `isPalindrome("${str}", ${left}, ${right})`;
    setCallStack(prev => [...prev, { call: callStr, status: 'pending' }]);

    setActiveLine(0);
    await delay(500);
    if (stopRef.current) throw new Error('stopped');

    if (left >= right) {
      setActiveLine(1);
      await delay(500);
      if (stopRef.current) throw new Error('stopped');

      setActiveLine(2);
      const step: Step = {
        left, right,
        leftChar: str[left] ?? '',
        rightChar: str[right] ?? '',
        match: true,
        phase: 'complete',
        depth,
      };
      setCurrentStep(step);
      setSteps(prev => [...prev, step]);

      setCallStack(prev => {
        const next = [...prev];
        next[next.length - 1] = { call: callStr, status: 'resolved', result: true };
        return next;
      });

      await delay(600);
      if (stopRef.current) throw new Error('stopped');
      return true;
    }

    setActiveLine(4);
    const lc = str[left], rc = str[right];
    const match = lc === rc;
    const step: Step = {
      left, right,
      leftChar: lc, rightChar: rc,
      match, phase: 'checking', depth,
    };

    setCurrentStep(step);
    setSteps(prev => [...prev, step]);
    await delay(700);
    if (stopRef.current) throw new Error('stopped');

    if (!match) {
      setActiveLine(5);
      step.phase = 'mismatch';
      setCurrentStep(step);

      setCallStack(prev => {
        const next = [...prev];
        next[next.length - 1] = { call: callStr, status: 'resolved', result: false };
        return next;
      });

      await delay(600);
      if (stopRef.current) throw new Error('stopped');
      return false;
    }

    setActiveLine(7);
    step.phase = 'match';
    setCurrentStep(step);
    await delay(500);
    if (stopRef.current) throw new Error('stopped');

    const res = await recurse(str, left + 1, right - 1, depth + 1);

    setCallStack(prev => {
      const next = [...prev];
      if (next.length > depth) {
        next[depth] = { call: callStr, status: 'resolved', result: res };
      }
      return next;
    });

    return res;
  };

  const executeCommand = async (cmd: RecursionCommands) => {
    switch (cmd.action) {
      case 'run': {
        if (isAnimating) return;
        const s = clean(inputValue);
        if (s.length === 0) { alert('Please enter a valid string.'); return; }
        if (s.length > 15) { alert('Please use 15 chars or fewer for best visualization.'); return; }

        reset();
        stopRef.current = false;
        setIsAnimating(true);
        setVisualString(s.split(''));

        try {
          const res = await recurse(s, 0, s.length - 1, 0);
          setFinalResult(res);
        } catch {
        } finally {
          setActiveLine(null);
          setIsAnimating(false);
          onComplete();
        }
        break;
      }
      case 'stop': {
        stopRef.current = true;
        setIsAnimating(false);
        onComplete();
        break;
      }
      case 'reset': {
        reset();
        setFinalResult(null);
        setIsAnimating(false);
        onComplete();
        break;
      }
      case 'generate': {
        break;
      }
    }
  };

  return (
    <div className={styles.palindromeViz}>
      <div className={styles.stringDisplay}>
        {visualString.map((char, index) => {
          const isLeft = currentStep && index === currentStep.left;
          const isRight = currentStep && index === currentStep.right;
          const isInRange = currentStep && index >= currentStep.left && index <= currentStep.right;

          return (
            <div
              key={index}
              className={`${styles.charBox} ${isLeft || isRight ? styles.activeChar : ''} ${
                isInRange ? styles.inRange : ''
              } ${
                currentStep && (isLeft || isRight) && currentStep.phase === 'match' ? styles.matchChar : ''
              } ${
                currentStep && (isLeft || isRight) && currentStep.phase === 'mismatch' ? styles.mismatchChar : ''
              }`}
            >
              <span className={styles.charValue}>{char}</span>
              <span className={styles.charIndex}>{index}</span>
              {isLeft && <span className={styles.pointer}>L</span>}
              {isRight && <span className={styles.pointer}>R</span>}
            </div>
          );
        })}
      </div>
      {finalResult !== null && (
      <div className={`${styles.palindromeResult} ${finalResult ? styles.isPalindrome : styles.notPalindrome}`}>
      {finalResult ? '✓ Is a palindrome!' : '✗ Not a palindrome'}
      </div>
      )}
    </div>
  );
};

export default PalindromeEngine;
